/**
 * Frontend Authentication Service
 * Calls backend API for all authentication operations
 * This ensures secure token handling on the server side
 */

import { API_BASE_URL, API_ENDPOINTS } from '../config/api'

const AUTH_API = `${API_BASE_URL}/api/auth`

/**
 * Get public auth configuration from backend
 * @returns {Promise<object>} {clientId, callbackUrl, authEndpoint, scopes}
 */
export const getAuthConfig = async () => {
  const response = await fetch(`${AUTH_API}/config`)
  if (!response.ok) {
    throw new Error('Failed to fetch auth config')
  }
  return response.json()
}

/**
 * Get OAuth authorization URL from backend
 * Backend generates PKCE parameters and stores them in session
 * @returns {Promise<object>} {authUrl, requestId, expiresIn}
 */
export const getAuthorizationUrl = async () => {
  const response = await fetch(`${AUTH_API}/authorize`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to get authorization URL')
  }

  return response.json()
}

/**
 * Handle OAuth callback by sending code to backend
 * Backend validates state, exchanges code for token, and stores it
 * @param {string} code - Authorization code from OAuth provider
 * @param {string} state - State parameter for CSRF validation
 * @param {string} requestId - Request ID for session tracking
 * @returns {Promise<object>} {success, user, tokens}
 */
export const handleOAuthCallback = async (code, state, requestId = null) => {
  const response = await fetch(`${AUTH_API}/callback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      code,
      state,
      requestId,
    }),
  })

  if (!response.ok) {
    const error = await response.json()
    const errorMsg = error.error || 'OAuth callback failed'
    const errorCode = error.code || 'UNKNOWN'
    const err = new Error(errorMsg)
    err.code = errorCode
    throw err
  }

  return response.json()
}

/**
 * Refresh access token using refresh token
 * Backend handles the refresh and token storage
 * @param {string} refreshToken - Refresh token
 * @param {string} userId - User ID
 * @returns {Promise<object>} {success, tokens}
 */
export const refreshAccessToken = async (refreshToken, userId) => {
  const response = await fetch(`${AUTH_API}/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      refreshToken,
      userId,
    }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to refresh token')
  }

  return response.json()
}

/**
 * Get authenticated user profile
 * Requires valid access token
 * @param {string} accessToken - Access token
 * @returns {Promise<object>} {success, profile}
 */
export const getUserProfile = async (accessToken) => {
  const response = await fetch(`${AUTH_API}/profile`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to get user profile')
  }

  return response.json()
}

/**
 * Check current authentication status
 * @returns {Promise<object>} {authenticated, user, expiresIn}
 */
export const getAuthStatus = async () => {
  const response = await fetch(`${AUTH_API}/status`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error('Failed to get auth status')
  }

  return response.json()
}

/**
 * Logout and clear tokens on backend
 * @param {string} userId - User ID
 * @returns {Promise<object>} {success, message}
 */
export const logout = async (userId) => {
  const response = await fetch(`${AUTH_API}/logout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      userId,
    }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to logout')
  }

  return response.json()
}

/**
 * Get OAuth authorization URL and redirect user
 * This is the main entry point for login
 */
export const initiateLogin = async () => {
  try {
    const { authUrl } = await getAuthorizationUrl()
    // Redirect to Autodesk OAuth
    window.location.href = authUrl
  } catch (error) {
    throw new Error(`Failed to initiate login: ${error.message}`)
  }
}

/**
 * Check if user has review/approval role in project
 * @param {string} accessToken - Access token
 * @param {string} projectId - Project ID
 * @returns {Promise<object>} {success, hasReviewRole, role, roleNames}
 */
export const checkUserRole = async (accessToken, projectId) => {
  const response = await fetch(`${AUTH_API}/user-role?projectId=${projectId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to check user role')
  }

  return response.json()
}

export default {
  getAuthConfig,
  getAuthorizationUrl,
  handleOAuthCallback,
  refreshAccessToken,
  getUserProfile,
  getAuthStatus,
  logout,
  initiateLogin,
  checkUserRole,
}
